import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Text, EmptyState, Modal } from '@teals/ui-web';
import {
  useGetBillingBudgetQuery,
  useGetBillingProfileQuery,
  useGetInvoicesQuery,
  useGetPaymentMethodsQuery,
  useUpdateBillingBudgetMutation,
  useUpdateBillingProfileMutation,
  type BillingProfile,
} from '../../store/api/billingApi';
import {
  AppBudgetRow,
  FieldGrid,
  Footnote,
  PanelCard,
  SectionTitle,
} from './Payments.styles';

const EMPTY_PROFILE: BillingProfile = {
  businessName: '',
  billingEmail: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  taxId: '',
};

export const BillingPanel: React.FC = () => {
  const { data: profile } = useGetBillingProfileQuery();
  const [updateProfile, { isLoading: savingProfile }] =
    useUpdateBillingProfileMutation();
  const { data: budget } = useGetBillingBudgetQuery();
  const [updateBudget, { isLoading: savingBudget }] =
    useUpdateBillingBudgetMutation();
  const { data: methods } = useGetPaymentMethodsQuery();
  const { data: invoices } = useGetInvoicesQuery();
  const [form, setForm] = useState<BillingProfile>(EMPTY_PROFILE);
  const [tokenCap, setTokenCap] = useState('');
  const [alertPct, setAlertPct] = useState('85');
  const [modalOpen, setModalOpen] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [profileHydrated, setProfileHydrated] = useState(false);
  const [budgetHydrated, setBudgetHydrated] = useState(false);

  useEffect(() => {
    if (profile && !profileHydrated) {
      setForm({
        businessName: profile.businessName || '',
        billingEmail: profile.billingEmail || '',
        addressLine1: profile.addressLine1 || '',
        addressLine2: profile.addressLine2 || '',
        city: profile.city || '',
        state: profile.state || '',
        postalCode: profile.postalCode || '',
        country: profile.country || '',
        taxId: profile.taxId || '',
      });
      setProfileHydrated(true);
    }
  }, [profile, profileHydrated]);

  useEffect(() => {
    if (budget && !budgetHydrated) {
      const cap = budget.organisation?.budgetConfig?.tokenCap;
      if (cap != null) setTokenCap(String(cap));
      const alert = budget.organisation?.budgetConfig?.alertThresholdPct;
      if (alert != null) setAlertPct(String(alert));
      setBudgetHydrated(true);
    }
  }, [budget, budgetHydrated]);

  const onSaveProfile = async () => {
    setSavedMsg('');
    await updateProfile(form).unwrap();
    setSavedMsg('Billing profile saved.');
  };

  const onSaveOrgBudget = async () => {
    setSavedMsg('');
    await updateBudget({
      budgetConfig: {
        tokenCap: tokenCap ? Number(tokenCap) : undefined,
        period: 'monthly',
        alertThresholdPct: alertPct ? Number(alertPct) : undefined,
      },
    }).unwrap();
    setSavedMsg('Organisation budget saved.');
  };

  const setField =
    (key: keyof BillingProfile) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const paymentMethods = methods?.paymentMethods || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <PanelCard padding={24}>
        <SectionTitle>Billing profile</SectionTitle>
        <FieldGrid>
          <Input
            label="Legal / business name"
            value={form.businessName}
            onChange={setField('businessName')}
          />
          <Input
            label="Billing contact email"
            value={form.billingEmail}
            onChange={setField('billingEmail')}
          />
          <Input
            label="Address line 1"
            value={form.addressLine1}
            onChange={setField('addressLine1')}
          />
          <Input
            label="Address line 2"
            value={form.addressLine2}
            onChange={setField('addressLine2')}
          />
          <Input label="City" value={form.city} onChange={setField('city')} />
          <Input label="State" value={form.state} onChange={setField('state')} />
          <Input
            label="Postal code"
            value={form.postalCode}
            onChange={setField('postalCode')}
          />
          <Input
            label="Country"
            value={form.country}
            onChange={setField('country')}
          />
          <Input label="Tax ID" value={form.taxId} onChange={setField('taxId')} />
        </FieldGrid>
        <Button variant="primary" onClick={onSaveProfile} disabled={savingProfile}>
          {savingProfile ? 'Saving…' : 'Save billing profile'}
        </Button>
      </PanelCard>

      <PanelCard padding={24}>
        <SectionTitle>Payment methods</SectionTitle>
        {paymentMethods.length === 0 ? (
          <EmptyState
            title="No payment methods yet"
            description="Card capture will be available when the payment provider is connected. Lumina never stores raw card numbers in the portal."
          />
        ) : null}
        <Button variant="outline" onClick={() => setModalOpen(true)}>
          Add payment method
        </Button>
      </PanelCard>

      <PanelCard padding={24}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <SectionTitle>Budgets</SectionTitle>
          <Link to="/analytics">View utilisation</Link>
        </div>
        <Footnote>
          Set an organisation-wide monthly <strong>token</strong> cap here.
          Application caps are edited on each application’s Configuration tab.
          Staff do not set these limits.
        </Footnote>
        <FieldGrid>
          <Input
            label="Monthly org token limit"
            value={tokenCap}
            onChange={(e) => setTokenCap(e.target.value)}
            placeholder="e.g. 10000000"
          />
          <Input
            label="Alert threshold (%)"
            value={alertPct}
            onChange={(e) => setAlertPct(e.target.value)}
            placeholder="85"
          />
        </FieldGrid>
        <Button variant="primary" onClick={onSaveOrgBudget} disabled={savingBudget}>
          {savingBudget ? 'Saving…' : 'Save org budget'}
        </Button>

        <Text variant="h4" style={{ marginTop: '0.5rem' }}>
          Application budgets
        </Text>
        {(budget?.applications || []).map((app) => (
          <AppBudgetRow key={app.applicationId}>
            <span>{app.name}</span>
            <span>
              cap{' '}
              {(app.budgetConfig as { tokenCap?: number } | null)?.tokenCap ?? '—'}{' '}
              · <Link to={`/applications/${app.applicationId}`}>Edit on app →</Link>
            </span>
          </AppBudgetRow>
        ))}
        {!budget?.applications?.length && (
          <Text variant="body2">No applications yet.</Text>
        )}
      </PanelCard>

      <PanelCard padding={24}>
        <SectionTitle>Invoices</SectionTitle>
        {(invoices?.invoices?.length ?? 0) === 0 ? (
          <EmptyState
            title="No invoices yet"
            description="Invoice history will appear here once billing provider invoices are generated."
          />
        ) : null}
      </PanelCard>

      {savedMsg ? (
        <Text variant="body2" style={{ color: 'var(--success, #15803d)' }}>
          {savedMsg}
        </Text>
      ) : null}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add Payment Method"
        message="Payment method integration will be available when the billing provider is connected."
        acceptButtonText="OK"
      />
    </div>
  );
};

export default BillingPanel;
